import styles from './UserDeleteConfirmationModal.module.css';

import React, { useMemo } from 'react';
import { Field, Form, Formik } from 'formik';
import { Modal } from 'imports/ReactBootstrapImport';
import Api from 'components/Api';
import BngCircleCheckbox from 'components/bng/form/BngCircleCheckbox';
import BngSelectSearch from 'components/bng/form/BngSelectSearch';
import BngField from 'components/bng/form/BngField';
import bngYup from 'components/bng/form/yup/BngYup';
import useTranslation from 'components/hooks/useTranslation';
import Utils from 'components/Utils';
import { BngCancelButton, BngDangerButton } from 'components/ui/Button';

const FormSchema = bngYup((yup) => {
  return yup.object({
    transferFolder: yup.boolean().default(false),
    userId: yup.number().when('transferFolder', {
      is: true,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.nullable(),
    }),
  });
});

function AlertMsgRender({ alertMessage = '', folderExists = false }) {
  const { t } = useTranslation();

  if (_.isEmpty(alertMessage)) {
    return <p style={!folderExists ? { textAlign: 'center' } : null}>{t('confirm.delete.user')}</p>;
  }

  if (_.isString(alertMessage)) {
    return (
      <div style={{ marginBottom: '15px', textAlign: 'center' }} dangerouslySetInnerHTML={{ __html: alertMessage }} />
    );
  }

  return alertMessage;
}

export default function UserDeleteConfirmationModal({
  user,
  projectUsers = [],
  onClose = _.noop,
  onConfirm = _.noop,
  alertMessage = '',
  userFieldLabel = '',
  folderExists = false,
}) {
  const { t } = useTranslation();

  const userOptions = useMemo(() => {
    return projectUsers
      .filter((u) => u.user.id !== user.id)
      .map((u) => {
        const label = Utils.Users.displayName(u.user);
        return {
          value: u.user.id,
          label,
          render: () => (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <img
                src={Api.buildUrl(u.user.avatarLink)}
                alt={`Avatar de ${label}`}
                style={{ width: '24px', height: '24px', marginRight: '8px', borderRadius: '50%' }}
              />
              <span>{label}</span>
            </div>
          ),
        };
      })
      .sort((a, b) => Utils.Strings.compareIgnoreCase(a.label, b.label));
  }, []);

  return (
    <Modal
      show={true}
      className={`UserDeleteConfirmationModal ${styles.ModalStyle}`}
      onHide={onClose}
      backdropClassName={styles.modalBackdrop}
      enforceFocus={false}
    >
      <div className={styles.DivTitleModal}>
        <img src={Api.buildUrl('/resources/images/icon_circled_dangerous.png')} alt="dangerous icon" />
        <span>{Utils.Users.displayName(user)}</span>
      </div>

      <Formik
        initialValues={FormSchema.default()}
        validationSchema={FormSchema}
        onSubmit={(values) => {
          onConfirm({ newUserId: values.transferFolder ? values.userId : null });
          onClose();
        }}
      >
        {({ values, setFieldValue }) => (
          <Form>
            <Modal.Body>
              <AlertMsgRender alertMessage={alertMessage} folderExists={folderExists} />

              <div className={styles.BngCircleCheckboxUserPage}>
                {folderExists &&
                  [
                    { value: false, label: t('delete.personal.folder') },
                    { value: true, label: t('transfer.personal.folder') },
                  ].map((opt, idx) => (
                    <div
                      key={idx}
                      className={styles.transferFolderOption}
                      onClick={() => setFieldValue('transferFolder', opt.value)}
                    >
                      <BngCircleCheckbox type="primary" field={{ value: opt.value === values.transferFolder }} />
                      <label>{opt.label}</label>
                    </div>
                  ))}
              </div>

              {values.transferFolder && (
                <div className="mt-2">
                  <Field
                    name="userId"
                    component={BngField}
                    inputComponent={BngSelectSearch}
                    required={true}
                    label={userFieldLabel ? userFieldLabel : t('user')}
                    options={userOptions}
                  />
                </div>
              )}
            </Modal.Body>

            <Modal.Footer>
              <div className={styles.ModalFooterUserPage}>
                <BngDangerButton type="submit">{t('confirm')}</BngDangerButton>
                <BngCancelButton className={`${styles.BngCancelButton}`} onClick={onClose}>
                  {t('cancel')}
                </BngCancelButton>
              </div>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
}
